import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { photos } from '../../data/site';
import { repertoire } from '../../data/repertoire';
import { ButtonLink } from '../ui/ButtonLink';
const ease = [0.23, 1, 0.32, 1] as const;
const songCount = Math.floor(repertoire.reduce((n, e) => n + e.songs.length, 0) / 10) * 10;
export function Hero() {
  const reduce = useReducedMotion();
  const fade = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 12
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      delay,
      ease
    }
  });
  return <section className="relative w-full overflow-hidden bg-ink pt-16 md:pt-20" data-id="element-68" data-mp="q44ymv1ftle">
      <p aria-hidden className="pointer-events-none absolute bottom-24 left-3 hidden origin-bottom-left -rotate-90 whitespace-nowrap text-[10px] uppercase tracking-[0.3em] text-muted xl:block" data-id="element-69" data-mp="s74bxi2yz2s">
        Huizen, Nederland — een muzikaal verhaal
      </p>

      <div className="relative mx-auto max-w-7xl px-5 pb-20 pt-8 md:px-10 lg:pb-28 lg:pt-6" data-id="element-70" data-mp="1q63kclp92f9h">
        <motion.p {...fade(0)} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-amber" data-id="element-71" data-mp="1muricp2n863z">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-amber" data-id="element-72" data-mp="1rt2sl0wuoimq" />
          Singer-songwriter · Piano- &amp; gitaardocent · Huizen
        </motion.p>

        <motion.p {...fade(0.05)} aria-hidden className="relative z-10 mt-4 font-serif text-[19vw] leading-[0.84] tracking-[-0.03em] text-paper lg:whitespace-nowrap lg:text-[min(13.5vw,172px)]" data-id="element-73" data-mp="1hy12o2ur3cj3">
          Bas <span className="italic text-amber" data-id="element-74" data-mp="2anrhke47iig9">Menninga</span>
        </motion.p>

        <motion.figure initial={reduce ? false : {
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.05,
        ease
      }} className="relative z-20 mt-10 w-full sm:mx-auto sm:w-2/3 lg:absolute lg:right-10 lg:top-10 lg:mt-0 lg:w-[27%]" data-id="element-75" data-mp="1vwr1po14rma3">
          <span aria-hidden className="absolute -top-2.5 left-3 h-full w-full rounded-md border border-amber-deep" data-id="element-76" data-mp="338eqd6xxa1va" />
          <img src={photos.stage} alt="Bas Menninga zingt achter de microfoon onder warm podiumlicht" style={{
          objectPosition: '38% 50%'
        }} className="relative aspect-[4/5] w-full rounded object-cover shadow-[0_24px_50px_rgba(0,0,0,0.55)] lg:aspect-[342/588]" data-id="element-77" data-mp="2y8sg96de51qp" />
          <figcaption className="absolute -bottom-6 -left-4 max-w-[230px] rounded border border-line bg-stage px-4 py-3" data-id="element-78" data-mp="2kakln53tucip">
            <span className="block font-serif text-base italic text-paper" data-id="element-79" data-mp="2fh9a4qo2l7kg">Live &amp; intiem</span>
            <span className="block text-[11px] text-muted" data-id="element-80" data-mp="2ak99wwdwiz19">Gitaar · piano · zang</span>
          </figcaption>
        </motion.figure>

        <div className="relative z-10 mt-16 grid grid-cols-1 gap-10 md:grid-cols-[1fr_1.3fr_1.6fr] md:gap-0 lg:mt-20 lg:w-[68%] lg:pr-6" data-id="element-81" data-mp="21j1kl3td58jb">
          <motion.div {...fade(0.1)} className="md:border-r md:border-line md:pr-6" data-id="element-82" data-mp="1wkzptlmkrks0">
            <p className="font-serif text-sm italic text-amber" data-id="element-83" data-mp="c2fk6ff8u5pd">01</p>
            <div className="mt-3 border-t border-line pt-5" data-id="element-84" data-mp="h10i2870wdan">
              <p className="font-serif text-4xl italic text-amber" data-id="element-85" data-mp="gzflye528mao">{songCount}+</p>
              <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-86" data-mp="m151o7389o53">nummers — van Hazes tot Hotel California</p>
            </div>
          </motion.div>

          <motion.div {...fade(0.15)} className="md:border-r md:border-line md:px-6" data-id="element-87" data-mp="3pgsn0vs2yuzb">
            <p className="font-serif text-sm italic text-amber" data-id="element-88" data-mp="3pezbb22m1kkg">02</p>
            <h1 className="mt-3 font-serif text-5xl leading-[1.08] text-paper md:text-[2.5rem]" data-id="element-89" data-mp="3ugewqv8p9t3d">
              <span className="sr-only" data-id="element-90" data-mp="2ajiyvhv1zgx">Bas Menninga — </span>
              De juiste <em className="text-amber" data-id="element-91" data-mp="732snhdfgagg">snaar</em> raken.
            </h1>
          </motion.div>

          <motion.div {...fade(0.2)} className="md:pl-6" data-id="element-92" data-mp="36r44611i750b">
            <p className="font-serif text-sm italic text-amber" data-id="element-93" data-mp="36ohxe3mfstj5">03</p>
            <p className="mt-3 text-[15px] leading-relaxed text-sand" data-id="element-94" data-mp="3aqczvbg3tcyi">
              Live muziek voor uw feest, borrel of viering — en piano- of gitaarles voor wie zelf wil leren spelen.
            </p>
            <div className="mt-6 flex flex-col gap-3" data-id="element-95" data-mp="2mprd2u6qqno9">
              <ButtonLink to="/optreden" arrow data-id="element-96" data-mp="31dmbmr7fjm99">
                Boek een optreden
              </ButtonLink>
              <ButtonLink to="/muziekles" variant="outline" data-id="element-97" data-mp="2wm45loqjuvhf">
                Muziekles? Kijk hier
              </ButtonLink>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}